import React, { useState, useEffect, useCallback } from 'react';
import API from '../../../API';

interface GameServerInstance {
  id: string;
  event_id: string;
  server_id: string;
  world_id: string;
  user_id?: string;
  game_server_instance_id?: string;
  world_socket_instance_id?: string;
  game_server_ip: string;
  world_socket_ip: string;
  status: string;
  server_name: string;
}

interface GameServerManagementProps {
  eventId: string;
  accessToken: string;
}

const GameServerManagement: React.FC<GameServerManagementProps> = ({
  eventId,
  accessToken
}) => {
  const [servers, setServers] = useState<GameServerInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stoppingIds, setStoppingIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Hardcoded world_id for now - will be configurable later
  const WORLD_ID = 'current';

  const fetchServers = useCallback(async () => {
    if (!accessToken || !eventId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await API.adminListGameServers(accessToken, eventId);
      if (response.ok) {
        const data = await response.json();
        setServers(data.game_server_instances || []);
      } else {
        const errorText = await response.text();
        setError(`Failed to fetch servers: ${errorText}`);
      }
    } catch (err) {
      setError(`Failed to fetch servers: ${err}`);
    } finally {
      setLoading(false);
    }
  }, [accessToken, eventId]);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  const handleStartServer = async () => {
    if (!accessToken || !eventId) return;
    
    setStarting(true);
    setError(null);
    try {
      const response = await API.adminStartGameServer(accessToken, {
        eventId,
        world_id: WORLD_ID
      });
      if (response.ok) {
        const data = await response.json();
        setServers(prev => [...prev, data.game_server_instance]);
      } else {
        const errorText = await response.text();
        setError(`Failed to start server: ${errorText}`);
      }
    } catch (err) {
      setError(`Failed to start server: ${err}`);
    } finally {
      setStarting(false);
    }
  };

  const handleStopServer = async (instanceId: string) => {
    if (!accessToken || !eventId) return;
    
    // Confirm before stopping
    if (!window.confirm(`Are you sure you want to stop this server? This will terminate the EC2 instances.`)) {
      return;
    }
    
    setStoppingIds(prev => new Set(prev).add(instanceId));
    setError(null);
    try {
      const response = await API.adminStopGameServer(accessToken, {
        eventId,
        instanceId
      });
      if (response.ok) {
        setServers(prev => prev.filter(s => s.id !== instanceId));
      } else {
        const errorText = await response.text();
        setError(`Failed to stop server: ${errorText}`);
      }
    } catch (err) {
      setError(`Failed to stop server: ${err}`);
    } finally {
      setStoppingIds(prev => {
        const next = new Set(prev);
        next.delete(instanceId);
        return next;
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
        return 'text-green-400';
      case 'pending':
      case 'starting':
        return 'text-yellow-400';
      case 'stopped':
      case 'terminated':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
        return 'bg-green-500/20 border-green-500/50 text-green-400';
      case 'pending':
      case 'starting':
        return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400';
      case 'stopped':
      case 'terminated':
        return 'bg-red-500/20 border-red-500/50 text-red-400';
      default:
        return 'bg-gray-500/20 border-gray-500/50 text-gray-400';
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-white/30">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold m-0 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-purple-500/20 border border-purple-500/50">
            <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          </span>
          Game Server Instances (EC2)
        </h3>
        <div className="flex gap-2">
          <button
            className="ff-button text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={fetchServers}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            className="ff-button text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed bg-green-600 hover:bg-green-700"
            onClick={handleStartServer}
            disabled={starting}
            style={{ backgroundColor: starting ? undefined : '#16a34a' }}
          >
            {starting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Starting (~2-5 min)...
              </span>
            ) : (
              '+ Start New Server'
            )}
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm">
        <strong>Note:</strong> Starting a server provisions new EC2 instances and takes 2-5 minutes. 
        World ID is currently hardcoded to "{WORLD_ID}".
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/20 border border-red-500/50 text-red-300">
          {error}
        </div>
      )}

      {/* Server List */}
      {loading && servers.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <svg className="animate-spin h-8 w-8 mx-auto mb-2" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading game servers...
        </div>
      ) : servers.length === 0 ? (
        <div className="text-center py-8 text-gray-400 border border-dashed border-gray-600 rounded-xl">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
          </svg>
          No game server instances running for this event.
          <br />
          <span className="text-sm">Click "Start New Server" to launch one.</span>
        </div>
      ) : (
        <div className="space-y-3">
          {servers.map((server) => (
            <div
              key={server.id}
              className="border border-white/20 rounded-xl p-4 bg-gray-800/50 hover:bg-gray-800/70 transition-colors"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <span className="font-bold text-lg">{server.server_name || 'Unnamed Server'}</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeColor(server.status)}`}>
                    {server.status}
                  </span>
                </div>
                <button
                  className="ff-button text-sm px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleStopServer(server.id)}
                  disabled={stoppingIds.has(server.id)}
                  style={{ backgroundColor: stoppingIds.has(server.id) ? undefined : '#dc2626' }}
                >
                  {stoppingIds.has(server.id) ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Stopping...
                    </span>
                  ) : (
                    'Stop Server'
                  )}
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <div className="flex flex-col">
                  <span className="text-gray-500 text-xs uppercase tracking-wide">Instance ID</span>
                  <span className="text-gray-300 font-mono text-xs break-all">{server.id}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-500 text-xs uppercase tracking-wide">Game Server IP</span>
                  <span className="text-gray-300 font-mono">{server.game_server_ip || 'Pending...'}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-500 text-xs uppercase tracking-wide">World Socket IP</span>
                  <span className="text-gray-300 font-mono">{server.world_socket_ip || 'Pending...'}</span>
                </div>
                {server.game_server_instance_id && (
                  <div className="flex flex-col">
                    <span className="text-gray-500 text-xs uppercase tracking-wide">EC2 Game Server</span>
                    <span className="text-gray-300 font-mono text-xs">{server.game_server_instance_id}</span>
                  </div>
                )}
                {server.world_socket_instance_id && (
                  <div className="flex flex-col">
                    <span className="text-gray-500 text-xs uppercase tracking-wide">EC2 World Socket</span>
                    <span className="text-gray-300 font-mono text-xs">{server.world_socket_instance_id}</span>
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-gray-500 text-xs uppercase tracking-wide">World ID</span>
                  <span className="text-gray-300 font-mono text-xs">{server.world_id}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GameServerManagement;

